import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import '../styles/node-designer.css';

const STORAGE_KEY_HTML = 'designer:html';
const STORAGE_KEY_CSS = 'designer:css';
const STORAGE_KEY_JS = 'designer:js';

const DEFAULT_HTML = `<div class="card">
  <h2>Hello, marketplace!</h2>
  <p>Use this canvas to prototype new node experiences.</p>
  <button id="cta">Click me</button>
</div>`;

const DEFAULT_CSS = `.canvas {
  min-height: 100vh;
  margin: 0;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #1d2671 0%, #c33764 100%);
  font-family: 'Inter', system-ui, sans-serif;
}

.card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  padding: 2.25rem 2.5rem;
  box-shadow: 0 20px 60px rgba(13, 23, 54, 0.25);
  max-width: 460px;
  text-align: center;
}

.card h2 {
  margin: 0 0 0.75rem 0;
  font-size: 1.85rem;
  color: #0f172a;
}

.card p {
  margin: 0 0 1.5rem 0;
  color: #4b5563;
  line-height: 1.5;
}

#cta {
  appearance: none;
  border: none;
  background: #2563eb;
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.75rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

#cta:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}`;

const DEFAULT_JS = `const button = document.getElementById('cta');

if (button) {
  button.addEventListener('click', () => {
    button.textContent = 'Clicked!';
    button.disabled = true;
    button.style.opacity = '0.7';
  });
}`;

function loadFromStorage(key: string, fallback: string) {
  try {
    const value = window.localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function stripStyleTags(css: string) {
  const trimmed = css.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.replace(/<style[^>]*>/gi, '').replace(/<\/style>/gi, '').trim();
}

function htmlToPlainText(html: string) {
  const trimmed = html.trim();
  if (!trimmed) {
    return '';
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'text/html');
    const text = doc.body?.textContent || '';
    return text.replace(/\s+/g, ' ').trim();
  } catch {
    return trimmed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function sanitizeDesignerHtml(html: string) {
  const source = typeof html === 'string' ? html : '';
  if (!source.trim()) {
    return '';
  }
  const replaceWithDiv = (content: string) =>
    content
      .replace(/<\s*form(\b[^>]*)?>/gi, (_match, attrs = '') => `<div${attrs || ''}>`)
      .replace(/<\/\s*form\s*>/gi, '</div>');

  if (typeof DOMParser === 'undefined') {
    return replaceWithDiv(source);
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'text/html');
    const forms = doc.querySelectorAll('form');
    forms.forEach((form) => {
      const replacement = doc.createElement('div');
      Array.from(form.attributes).forEach((attr) => {
        replacement.setAttribute(attr.name, attr.value);
      });
      replacement.innerHTML = form.innerHTML;
      form.parentNode?.replaceChild(replacement, form);
    });
    return doc.body?.innerHTML ?? replaceWithDiv(source);
  } catch {
    return replaceWithDiv(source);
  }
}

const PREVENT_DEFAULT_REGEX = /\bpreventDefault\s*\(/i;

const BUTTON_PREVENT_DEFAULT_GUARD = `(function () {
  var MARK_ATTRIBUTE = 'data-node-designer-prevent-default';
  function guardButton(button) {
    if (!button || button.getAttribute(MARK_ATTRIBUTE) === 'true') {
      return;
    }
    button.setAttribute(MARK_ATTRIBUTE, 'true');
    button.addEventListener('click', function (event) {
      if (!event.defaultPrevented) {
        event.preventDefault();
      }
    });
  }
  function guardWithin(root) {
    if (!root) {
      return;
    }
    if (root.tagName && root.tagName.toLowerCase() === 'button') {
      guardButton(root);
    }
    if (root.querySelectorAll) {
      var buttons = root.querySelectorAll('button');
      for (var i = 0; i < buttons.length; i += 1) {
        guardButton(buttons[i]);
      }
    }
  }
  function activate() {
    guardWithin(document);
    if (!(window && window.MutationObserver)) {
      return;
    }
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j += 1) {
          var node = nodes[j];
          if (node && node.nodeType === 1) {
            guardWithin(node);
          }
        }
      }
    });
    observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', activate, { once: true });
  } else {
    activate();
  }
})();`;

function ensureButtonPreventDefault(code: string) {
  const trimmed = code.trim();
  if (!trimmed && !BUTTON_PREVENT_DEFAULT_GUARD) {
    return '';
  }
  if (!trimmed || !PREVENT_DEFAULT_REGEX.test(trimmed)) {
    return BUTTON_PREVENT_DEFAULT_GUARD + (trimmed ? `\n\n${trimmed}` : '');
  }
  return trimmed;
}

async function copyJsonToClipboard(payload: unknown) {
  const serialized = JSON.stringify(payload, null, 2);
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(serialized);
      return true;
    }
  } catch (error) {
    console.warn('Navigator clipboard API failed, falling back to legacy copy.', error);
  }

  try {
    if (typeof document === 'undefined') {
      return false;
    }
    const textarea = document.createElement('textarea');
    textarea.value = serialized;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const result = document.execCommand('copy');
    document.body.removeChild(textarea);
    return result;
  } catch (error) {
    console.error('Failed to copy JSON to clipboard via fallback.', error);
    return false;
  }
}

type ExportStatus = { tone: 'success' | 'error'; message: string };

type CurlTarget = 'BE' | 'FE';

interface ParsedCurl {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  ignored: string[];
}

function tokenizeCurl(input: string): string[] {
  const normalized = input.replace(/\\\r?\n/g, ' ');
  const tokens: string[] = [];
  let i = 0;
  const n = normalized.length;

  while (i < n) {
    while (i < n && /\s/.test(normalized[i])) {
      i += 1;
    }
    if (i >= n) {
      break;
    }
    let token = '';
    while (i < n && !/\s/.test(normalized[i])) {
      const ch = normalized[i];
      if (ch === "'") {
        i += 1;
        while (i < n && normalized[i] !== "'") {
          token += normalized[i];
          i += 1;
        }
        if (i < n) {
          i += 1;
        }
      } else if (ch === '"') {
        i += 1;
        while (i < n && normalized[i] !== '"') {
          if (normalized[i] === '\\' && i + 1 < n) {
            const next = normalized[i + 1];
            if (next === '"' || next === '\\' || next === '$' || next === '`') {
              token += next;
              i += 2;
              continue;
            }
            if (next === 'n') {
              token += '\n';
              i += 2;
              continue;
            }
            if (next === 't') {
              token += '\t';
              i += 2;
              continue;
            }
            if (next === 'r') {
              token += '\r';
              i += 2;
              continue;
            }
          }
          token += normalized[i];
          i += 1;
        }
        if (i < n) {
          i += 1;
        }
      } else if (ch === '\\' && i + 1 < n) {
        token += normalized[i + 1];
        i += 2;
      } else {
        token += ch;
        i += 1;
      }
    }
    tokens.push(token);
  }
  return tokens;
}

const CURL_FLAGS_WITH_ARG = new Set([
  '-F',
  '--form',
  '--form-string',
  '-u',
  '--user',
  '--cookie',
  '-b',
  '--data-urlencode',
  '-o',
  '--output',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '--cert',
  '--key',
  '--cacert',
  '--proxy',
  '-x',
  '--resolve',
  '--connect-timeout',
  '--max-time',
  '-m'
]);

const CURL_FLAGS_IGNORED_SILENTLY = new Set([
  '-L',
  '--location',
  '--compressed',
  '-k',
  '--insecure',
  '-s',
  '--silent',
  '-v',
  '--verbose',
  '-i',
  '--include',
  '-f',
  '--fail',
  '-g',
  '--globoff'
]);

function parseCurl(input: string): ParsedCurl {
  const tokens = tokenizeCurl(input.trim());
  if (tokens.length === 0) {
    throw new Error('Empty curl command.');
  }

  const start = tokens[0].toLowerCase() === 'curl' ? 1 : 0;
  let url = '';
  let method = '';
  const headers: Record<string, string> = {};
  let body = '';
  const ignored: string[] = [];

  for (let i = start; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (t === '-X' || t === '--request') {
      method = (tokens[i + 1] || '').toUpperCase();
      i += 1;
    } else if (t === '-H' || t === '--header') {
      const raw = tokens[i + 1] || '';
      i += 1;
      const idx = raw.indexOf(':');
      if (idx >= 0) {
        const key = raw.slice(0, idx).trim();
        const value = raw.slice(idx + 1).trim();
        if (key) {
          headers[key] = value;
        }
      }
    } else if (
      t === '-d' ||
      t === '--data' ||
      t === '--data-raw' ||
      t === '--data-binary' ||
      t === '--data-ascii'
    ) {
      body = tokens[i + 1] || '';
      i += 1;
    } else if (t === '--url') {
      url = tokens[i + 1] || '';
      i += 1;
    } else if (CURL_FLAGS_IGNORED_SILENTLY.has(t)) {
      // no-op
    } else if (t.startsWith('-')) {
      ignored.push(t);
      if (CURL_FLAGS_WITH_ARG.has(t)) {
        i += 1;
      }
    } else if (!url) {
      url = t;
    }
  }

  if (!url) {
    throw new Error('No URL found in curl command.');
  }

  if (!method) {
    method = body ? 'POST' : 'GET';
  }

  return { url, method, headers, body, ignored };
}

function deriveFlowVariable(url: string): string {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || parsed.hostname || 'apiCall';
    const cleaned = last
      .replace(/[^a-zA-Z0-9 _-]/g, '')
      .replace(/[-_\s]+(.)/g, (_, ch: string) => ch.toUpperCase())
      .replace(/^[^a-zA-Z_]+/, '');
    return cleaned || 'apiCall';
  } catch {
    return 'apiCall';
  }
}

function buildCurlNodePayload(parsed: ParsedCurl, target: CurlTarget) {
  const flowVariable = deriveFlowVariable(parsed.url);
  const title = `${parsed.method} ${flowVariable}`;
  const baseId = Date.now();

  if (target === 'BE') {
    return {
      list: [
        {
          id: baseId,
          data: {
            data: parsed.body,
            data_keys_to_cypher: [],
            flow_variable: flowVariable,
            frontend_available: true,
            headers: parsed.headers,
            is_backend: true,
            keys_to_store: ['data', 'status'],
            method: parsed.method,
            type: 'BE',
            url: parsed.url
          },
          inputs: { Input: { connections: [] } },
          outputs: {
            Success: { connections: [] },
            Failure: { connections: [] }
          },
          position: [0, 0],
          name: 'api_be',
          meta: { title, label: 'api_be', tags: [] }
        }
      ]
    };
  }

  return {
    list: [
      {
        id: baseId,
        data: {
          url: parsed.url,
          method: parsed.method,
          data: parsed.body,
          frontend_available: false,
          headers: parsed.headers,
          keys_to_store: ['data', 'status', 'statusText', 'headers', 'config']
        },
        inputs: { Input: { connections: [] } },
        outputs: {
          Success: { connections: [] },
          Failure: { connections: [] }
        },
        position: [0, 0],
        name: 'api_fe',
        meta: { title }
      }
    ]
  };
}

function buildMailerDocument(html: string, css: string) {
  const source = html.trim();
  const cssContent = css.trim();
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(source || '<body></body>', 'text/html');
    if (source && !/<body[\s>]/i.test(source)) {
      doc.body.innerHTML = source;
    }
    if (cssContent) {
      const style = doc.createElement('style');
      style.textContent = `\n${cssContent}\n`;
      doc.head.appendChild(style);
    }
    return doc.documentElement.outerHTML;
  } catch {
    const bodyBlock = source && /<body[\s>]/i.test(source)
      ? source
      : `<body>${source}</body>`;
    const headBlock = cssContent
      ? `<head>\n<style>\n${cssContent}\n</style>\n</head>`
      : '<head></head>';
    return `<html>\n${headBlock}\n${bodyBlock}\n</html>`;
  }
}

function extractCssFromHeadElements(headElements: unknown) {
  if (!Array.isArray(headElements)) {
    return '';
  }
  const parts = headElements
    .map((element) => {
      if (typeof element !== 'string') {
        return '';
      }
      return stripStyleTags(element);
    })
    .filter((segment) => segment.trim().length > 0);
  return parts.join('\n\n');
}

function parseMailerImport(emailHtml: string) {
  const fallback = {
    html: emailHtml || '',
    css: ''
  };
  if (!emailHtml || typeof emailHtml !== 'string') {
    return fallback;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(emailHtml, 'text/html');
    const styles = Array.from(doc.querySelectorAll('style'))
      .map((style) => style.textContent || '')
      .filter((text) => text.trim().length > 0)
      .join('\n\n');
    const bodyHtml = doc.body?.innerHTML ?? emailHtml;
    return {
      html: bodyHtml,
      css: styles
    };
  } catch {
    const styleMatches = [...emailHtml.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)].map(
      (match) => (match[1] || '').trim()
    );
    const styles = styleMatches.filter((text) => text.length > 0).join('\n\n');
    const bodyMatch = emailHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return {
      html: bodyMatch ? bodyMatch[1].trim() : emailHtml,
      css: styles
    };
  }
}

export function NodeDesignerPage() {
  const [html, setHtml] = useState(() => loadFromStorage(STORAGE_KEY_HTML, DEFAULT_HTML));
  const [css, setCss] = useState(() => loadFromStorage(STORAGE_KEY_CSS, DEFAULT_CSS));
  const [js, setJs] = useState(() => loadFromStorage(STORAGE_KEY_JS, DEFAULT_JS));
  const [showEditors, setShowEditors] = useState(true);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [resetConfirming, setResetConfirming] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [curlModalOpen, setCurlModalOpen] = useState(false);
  const [curlText, setCurlText] = useState('');
  const [curlTarget, setCurlTarget] = useState<CurlTarget>('BE');
  const [curlError, setCurlError] = useState<string | null>(null);
  const resetButtonRef = useRef<HTMLButtonElement>(null);
  const exportStatusTimeoutRef = useRef<number | null>(null);
  const importTextareaRef = useRef<HTMLTextAreaElement>(null);
  const curlTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_HTML, html);
    } catch {
      /* ignore */
    }
  }, [html]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_CSS, css);
    } catch {
      /* ignore */
    }
  }, [css]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_JS, js);
    } catch {
      /* ignore */
    }
  }, [js]);

  useEffect(() => {
    if (!resetConfirming) {
      return undefined;
    }
    const handlePointerDown = (event: PointerEvent) => {
      if (resetButtonRef.current && !resetButtonRef.current.contains(event.target as Node)) {
        setResetConfirming(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [resetConfirming]);

  useEffect(() => {
    return () => {
      if (exportStatusTimeoutRef.current !== null) {
        window.clearTimeout(exportStatusTimeoutRef.current);
      }
    };
  }, []);

  const scheduleExportStatusReset = useCallback(() => {
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
    }
    exportStatusTimeoutRef.current = window.setTimeout(() => {
      setExportStatus(null);
      exportStatusTimeoutRef.current = null;
    }, 2500);
  }, []);

  const cssContent = useMemo(() => stripStyleTags(css), [css]);
  const sanitizedHtml = useMemo(() => sanitizeDesignerHtml(html), [html]);
  const htmlContent = useMemo(() => (sanitizedHtml.trim() ? sanitizedHtml : ''), [sanitizedHtml]);
  const jsContent = useMemo(() => ensureButtonPreventDefault(js), [js]);

  const previewSrcDoc = useMemo(() => {
    const escapedJs = jsContent.replace(/<\/script/gi, '<\\/script');
    return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body {
        height: 100%;
      }
      body {
        margin: 0;
      }
      .canvas {
        min-height: 100%;
      }
      ${cssContent}
    </style>
  </head>
  <body>
    <div class="canvas">
      ${htmlContent}
    </div>
    <script>
      try {
        ${escapedJs}
      } catch (error) {
        const pre = document.createElement('pre');
        pre.style.cssText = 'background:#fee2e2;color:#991b1b;padding:12px;border-radius:8px;max-width:90%;margin:16px auto;font-family:monospace;';
        pre.textContent = 'Error executing script:\\n' + (error && error.stack ? error.stack : error);
        document.body.appendChild(pre);
        console.error(error);
      }
    </script>
  </body>
</html>`;
  }, [htmlContent, cssContent, jsContent]);

  const handleReset = useCallback(() => {
    if (!resetConfirming) {
      setResetConfirming(true);
      return;
    }
    setHtml(DEFAULT_HTML);
    setCss(DEFAULT_CSS);
    setJs(DEFAULT_JS);
    setResetConfirming(false);
    setExportStatus(null);
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
      exportStatusTimeoutRef.current = null;
    }
  }, [resetConfirming]);

  const handleRefreshPreview = useCallback(() => {
    setPreviewNonce((current) => current + 1);
  }, []);

  const importNodeData = useCallback(
    (payload: unknown) => {
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid node payload.');
      }
      const list = Array.isArray((payload as { list?: unknown }).list)
        ? (payload as { list: unknown[] }).list
        : [];
      if (list.length === 0 || typeof list[0] !== 'object' || list[0] === null) {
        throw new Error('Invalid node payload.');
      }
      const entry = list[0] as { data?: Record<string, unknown> };
      const data = entry.data;
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid node payload.');
      }

      if (
        data.type === 'FE' ||
        data.type === 'fe' ||
        data.is_backend === false ||
        (typeof data.html === 'string' && typeof data.script === 'string')
      ) {
        const importedHtml =
          typeof data.html === 'string'
            ? data.html
            : typeof (data as { template?: unknown }).template === 'string'
            ? ((data as { template: string }).template)
            : '';
        const importedCss = extractCssFromHeadElements(
          (data as { head_elements?: unknown; headElements?: unknown }).head_elements ??
            (data as { head_elements?: unknown; headElements?: unknown }).headElements
        );
        const importedJs = typeof data.script === 'string' ? data.script : '';
        setHtml(importedHtml || '');
        setCss(importedCss || '');
        setJs(importedJs || '');
        return 'Imported web node JSON.';
      }

      if (
        data.type === 'BE' ||
        data.type === 'be' ||
        data.is_backend === true ||
        typeof (data as { email_html?: unknown }).email_html === 'string' ||
        typeof (data as { emailHtml?: unknown }).emailHtml === 'string'
      ) {
        const emailHtml =
          typeof (data as { email_html?: unknown }).email_html === 'string'
            ? ((data as { email_html: string }).email_html || '')
            : typeof (data as { emailHtml?: unknown }).emailHtml === 'string'
            ? ((data as { emailHtml: string }).emailHtml || '')
            : '';
        const { html: mailerHtml, css: mailerCss } = parseMailerImport(emailHtml);
        setHtml(mailerHtml || '');
        setCss(mailerCss || '');
        setJs('');
        return 'Imported mailer node JSON.';
      }

      throw new Error('Unsupported node format.');
    },
    [setHtml, setCss, setJs]
  );

  const handleImportConfirm = useCallback(() => {
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
      exportStatusTimeoutRef.current = null;
    }
    setExportStatus(null);
    try {
      const parsed = JSON.parse(importText);
      const message = importNodeData(parsed);
      setShowEditors(true);
      setResetConfirming(false);
      setImportModalOpen(false);
      setImportText('');
      setImportError(null);
      setExportStatus({ tone: 'success', message });
      scheduleExportStatusReset();
    } catch (error) {
      console.error('Failed to import node payload', error);
      setImportError('Unable to import node. Check the JSON format and try again.');
    }
  }, [importNodeData, importText, scheduleExportStatusReset, setShowEditors, setResetConfirming]);

  const handleImportCancel = useCallback(() => {
    setImportModalOpen(false);
    setImportText('');
    setImportError(null);
  }, []);

  const curlPreview = useMemo(() => {
    const trimmed = curlText.trim();
    if (!trimmed) {
      return { json: '', parsed: null as ParsedCurl | null, error: null as string | null };
    }
    try {
      const parsed = parseCurl(trimmed);
      const payload = buildCurlNodePayload(parsed, curlTarget);
      return { json: JSON.stringify(payload, null, 2), parsed, error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to parse curl command.';
      return { json: '', parsed: null, error: message };
    }
  }, [curlText, curlTarget]);

  const handleCurlOpen = useCallback(() => {
    setCurlModalOpen(true);
    setCurlText('');
    setCurlError(null);
  }, []);

  const handleCurlCancel = useCallback(() => {
    setCurlModalOpen(false);
    setCurlText('');
    setCurlError(null);
  }, []);

  const handleCurlCopy = useCallback(() => {
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
      exportStatusTimeoutRef.current = null;
    }
    setExportStatus(null);
    const trimmed = curlText.trim();
    if (!trimmed) {
      setCurlError('Paste a curl command first.');
      return;
    }
    let parsed: ParsedCurl;
    try {
      parsed = parseCurl(trimmed);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to parse curl command.';
      setCurlError(message);
      return;
    }
    const payload = buildCurlNodePayload(parsed, curlTarget);
    copyJsonToClipboard(payload)
      .then((copied) => {
        if (copied) {
          const label = curlTarget === 'BE' ? 'backend' : 'frontend';
          setCurlModalOpen(false);
          setCurlText('');
          setCurlError(null);
          setExportStatus({
            tone: 'success',
            message: `Copied ${label} API node JSON to clipboard.`
          });
          scheduleExportStatusReset();
        } else {
          setCurlError('Unable to copy JSON to clipboard.');
        }
      })
      .catch(() => {
        setCurlError('Unable to copy JSON to clipboard.');
      });
  }, [curlText, curlTarget, scheduleExportStatusReset]);

  useEffect(() => {
    if (importModalOpen) {
      const id = window.setTimeout(() => {
        importTextareaRef.current?.focus();
      }, 0);
      return () => {
        window.clearTimeout(id);
      };
    }
    return undefined;
  }, [importModalOpen]);

  useEffect(() => {
    if (!importModalOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleImportCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [importModalOpen, handleImportCancel]);

  useEffect(() => {
    if (curlModalOpen) {
      const id = window.setTimeout(() => {
        curlTextareaRef.current?.focus();
      }, 0);
      return () => {
        window.clearTimeout(id);
      };
    }
    return undefined;
  }, [curlModalOpen]);

  useEffect(() => {
    if (!curlModalOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCurlCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [curlModalOpen, handleCurlCancel]);

  const handleExportWebNode = useCallback(() => {
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
      exportStatusTimeoutRef.current = null;
    }
    setExportStatus(null);
    const cssBlock = cssContent ? [`<style>\n${cssContent}\n</style>`] : [];
    const payload = {
      list: [
        {
          id: Date.now(),
          data: {
            is_backend: false,
            data_keys_to_cypher: [],
            type: 'FE',
            vuetify_form: false,
            form_structure: [
              {
                name: 'button',
                id: 'next',
                _id: 'next',
                label: 'Next',
                sizes: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
                use_theme_primary_color: true
              }
            ],
            html: htmlContent,
            head_elements: cssBlock,
            temporary_head_elements: [],
            keys_to_store: [],
            frontend_available: false,
            submit_elements_ids: {},
            submit_elements_ids_order: { 0: 'Failure' },
            script: jsContent
          },
          inputs: { Input: { connections: [] } },
          outputs: { Failure: { connections: [] } },
          position: [0, 0],
          name: 'fe_form',
          meta: { label: 'fe_form', title: '' }
        }
      ]
    };

    copyJsonToClipboard(payload)
      .then((copied) => {
        if (copied) {
          setExportStatus({
            tone: 'success',
            message: 'Copied web node JSON to clipboard.'
          });
          scheduleExportStatusReset();
        } else {
          setExportStatus({
            tone: 'error',
            message: 'Unable to copy web node JSON. Please copy manually.'
          });
          scheduleExportStatusReset();
        }
      })
      .catch(() => {
        setExportStatus({
          tone: 'error',
          message: 'Unable to copy web node JSON. Please copy manually.'
        });
        scheduleExportStatusReset();
      });
  }, [cssContent, htmlContent, jsContent, scheduleExportStatusReset]);

  const handleExportMailerNode = useCallback(() => {
    if (exportStatusTimeoutRef.current !== null) {
      window.clearTimeout(exportStatusTimeoutRef.current);
      exportStatusTimeoutRef.current = null;
    }
    setExportStatus(null);
    const emailHtml = buildMailerDocument(htmlContent, cssContent);
    const emailText = htmlToPlainText(htmlContent);
    const payload = {
      list: [
        {
          id: Date.now(),
          data: {
            is_backend: true,
            data_keys_to_cypher: [],
            type: 'BE',
            email_html: emailHtml,
            email_text: emailText,
            transport_from_env: true
          },
          inputs: { Input: { connections: [] } },
          outputs: { Success: { connections: [] }, Failure: { connections: [] } },
          position: [0, 0],
          name: 'send_email_v2',
          meta: { label: 'send_email_v2', title: '' }
        }
      ]
    };

    copyJsonToClipboard(payload)
      .then((copied) => {
        if (copied) {
          setExportStatus({
            tone: 'success',
            message: 'Copied mailer node JSON to clipboard.'
          });
          scheduleExportStatusReset();
        } else {
          setExportStatus({
            tone: 'error',
            message: 'Unable to copy mailer node JSON. Please copy manually.'
          });
          scheduleExportStatusReset();
        }
      })
      .catch(() => {
        setExportStatus({
          tone: 'error',
          message: 'Unable to copy mailer node JSON. Please copy manually.'
        });
        scheduleExportStatusReset();
      });
  }, [cssContent, htmlContent, scheduleExportStatusReset]);
  const handleOpenPreview = useCallback(() => {
    try {
      const blob = new Blob([previewSrcDoc], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const previewWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (previewWindow) {
        previewWindow.focus();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        URL.revokeObjectURL(url);
        console.warn('Popup blocked when opening full preview.');
      }
    } catch (error) {
      console.error('Failed to open full preview', error);
    }
  }, [previewSrcDoc]);

  const workspaceClassName = useMemo(
    () =>
      `node-designer__workspace${showEditors ? '' : ' node-designer__workspace--preview-only'}`,
    [showEditors]
  );

  return (
    <div className="node-designer">
      <header className="node-designer__header">
        <div>
          <h1>Node Designer</h1>
          <p>
            Prototype custom node experiences with live HTML, CSS, and JavaScript. Use the preview to
            validate behaviour before embedding the node in a flow.
          </p>
        </div>
        <div className="node-designer__header-actions">
          <div className="node-designer__action-group">
            <span className="node-designer__action-group-label">Import & export</span>
            <button
              type="button"
              className="button-tertiary"
              onClick={() => {
                setImportModalOpen(true);
                setImportText('');
                setImportError(null);
              }}
            >
              Import node
            </button>
            <button type="button" className="button-tertiary" onClick={handleCurlOpen}>
              Import from curl
            </button>
            <button type="button" className="button-tertiary" onClick={handleExportWebNode}>
              Copy web node JSON
            </button>
            <button type="button" className="button-tertiary" onClick={handleExportMailerNode}>
              Copy mailer node JSON
            </button>
          </div>

          {exportStatus ? (
            <span className={`copy-feedback copy-feedback-${exportStatus.tone}`}>
              {exportStatus.message}
            </span>
          ) : null}
        </div>
      </header>

      <div className={workspaceClassName}>
        <div className="node-designer__workspace-actions">
          <div className="node-designer__action-group node-designer__action-group--workspace">
            <span className="node-designer__action-group-label">Workspace</span>
            <button
              type="button"
              className="button-tertiary"
              onClick={() => setShowEditors((current) => !current)}
            >
              {showEditors ? 'Hide code panels' : 'Show code panels'}
            </button>
            <button
              type="button"
              ref={resetButtonRef}
              className={`button button-secondary node-designer__reset-button${resetConfirming ? ' node-designer__reset-button--confirm' : ''}`}
              onClick={handleReset}
            >
              {resetConfirming ? 'Click again to reset' : 'Reset to default'}
            </button>
          </div>
        </div>

        {showEditors ? (
          <section className="node-designer__editors">
            <EditorPanel
              label="HTML"
              value={html}
              onChange={setHtml}
              placeholder="<div>Hello world</div>"
              language="html"
            />
            <EditorPanel
              label="CSS"
              value={css}
              onChange={setCss}
              placeholder="body { background: white; }"
              language="css"
            />
            <EditorPanel
              label="JavaScript"
              value={js}
              onChange={setJs}
              placeholder="console.log('Hello node designer');"
              language="javascript"
            />
          </section>
        ) : null}

        <section className="node-designer__preview">
          <div className="node-designer__preview-header">
            <div className="node-designer__preview-text">
              <h2>Live preview</h2>
              <p>Preview updates automatically as you type.</p>
            </div>
            <div className="node-designer__preview-actions">
              <button type="button" className="button-tertiary" onClick={handleRefreshPreview}>
                Reset preview
              </button>
              <button type="button" className="button-tertiary" onClick={handleOpenPreview}>
                Open full preview
              </button>
            </div>
          </div>
          <iframe
            title="Node preview"
            className="node-designer__preview-frame"
            sandbox="allow-scripts"
            srcDoc={previewSrcDoc}
            key={previewNonce}
          />
        </section>
      </div>
      {importModalOpen ? (
        <div
          className="node-designer__import-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleImportCancel();
            }
          }}
        >
          <div className="node-designer__import-modal" role="dialog" aria-modal="true">
            <header className="node-designer__import-header">
              <h2>Import node JSON</h2>
              <p>Paste a JSON exported from the marketplace or IO.</p>
            </header>
            <label className="node-designer__import-label" htmlFor="designer-import-text">
              Node JSON
            </label>
            <textarea
              id="designer-import-text"
              ref={importTextareaRef}
              className="node-designer__import-textarea"
              value={importText}
              onChange={(event) => {
                setImportText(event.target.value);
                setImportError(null);
              }}
              placeholder='{"list":[{...}]}'
              rows={14}
              spellCheck={false}
            />
            {importError ? <div className="node-designer__import-error">{importError}</div> : null}
            <div className="node-designer__import-actions">
              <button type="button" className="button-tertiary" onClick={handleImportCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={handleImportConfirm}
                disabled={!importText.trim()}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {curlModalOpen ? (
        <div
          className="node-designer__import-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleCurlCancel();
            }
          }}
        >
          <div className="node-designer__import-modal" role="dialog" aria-modal="true">
            <header className="node-designer__import-header">
              <h2>Import from curl</h2>
              <p>
                Paste a curl command and choose where the request will run. Supports method, URL,
                headers, and JSON body. Edit the JSON after pasting it into IO.
              </p>
            </header>
            <div className="node-designer__curl-target">
              <span className="node-designer__import-label">Target</span>
              <div className="node-designer__curl-target-options">
                <label className="node-designer__curl-target-option">
                  <input
                    type="radio"
                    name="curl-target"
                    value="BE"
                    checked={curlTarget === 'BE'}
                    onChange={() => setCurlTarget('BE')}
                  />
                  <span>Backend (api_be)</span>
                </label>
                <label className="node-designer__curl-target-option">
                  <input
                    type="radio"
                    name="curl-target"
                    value="FE"
                    checked={curlTarget === 'FE'}
                    onChange={() => setCurlTarget('FE')}
                  />
                  <span>Frontend (api_fe)</span>
                </label>
              </div>
            </div>
            <label className="node-designer__import-label" htmlFor="designer-curl-text">
              Curl command
            </label>
            <textarea
              id="designer-curl-text"
              ref={curlTextareaRef}
              className="node-designer__import-textarea"
              value={curlText}
              onChange={(event) => {
                setCurlText(event.target.value);
                setCurlError(null);
              }}
              placeholder={"curl 'https://example.com/api/login' \\\n  -H 'Content-Type: application/json' \\\n  --data-raw '{\"username\":\"alice\"}'"}
              rows={10}
              spellCheck={false}
            />
            {curlPreview.parsed && curlPreview.parsed.ignored.length > 0 ? (
              <div className="node-designer__curl-warning">
                Ignored flags: {curlPreview.parsed.ignored.join(', ')}
              </div>
            ) : null}
            {curlPreview.error ? (
              <div className="node-designer__import-error">{curlPreview.error}</div>
            ) : null}
            {curlPreview.json ? (
              <>
                <span className="node-designer__import-label">Generated node JSON</span>
                <pre className="node-designer__curl-preview">{curlPreview.json}</pre>
              </>
            ) : null}
            {curlError ? <div className="node-designer__import-error">{curlError}</div> : null}
            <div className="node-designer__import-actions">
              <button type="button" className="button-tertiary" onClick={handleCurlCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={handleCurlCopy}
                disabled={!curlText.trim() || Boolean(curlPreview.error)}
              >
                Copy node JSON
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface EditorPanelProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  language: 'html' | 'css' | 'javascript';
}

function EditorPanel({ label, value, onChange, placeholder, language }: EditorPanelProps) {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const id = `designer-${language}`;

  return (
    <div className="node-designer__editor">
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        spellCheck={false}
        className={`node-designer__textarea node-designer__textarea--${language}`}
      />
    </div>
  );
}
